
import {
	AddDataSharedFunc, DashboardSharedFilterFunc, DashboardSharedFilterKey,
	IDashboardSharedChartData, IDashboardSharedDimensionsViewModel,
} from "models/common";
import { IFilters, IFilterKeys, DimensionsById, ISourceData, IDimFacility, IDimension, IDimMDT } from "models/core";
import { IDashboardViewModel } from "./viewModel";

//Plots for this dashboard
import * as PrimarySite from "./primarySiteHierarchy";
import * as Morphology from "./morphology";
import * as Remoteness from "../../common/plotdata/remoteness";
import * as HHSOfResidence from "../../common/plotdata/hhsOfResidence";

export type { IDashboardViewModel } from "./viewModel";

export type IDashboardSource = IDashboardViewModel[];

export interface IBreastIndicatorConstants {
	totalBreastDiagnoses: number,
	upper95Percent: number,
	lower95Percent: number,
	upper98Percent: number,
	lower98Percent: number,
	statePercent: number
}

export interface IDashboardDimensionsViewModel extends IDashboardSharedDimensionsViewModel {
	defMastectomyArray?: Array<IBreastIndicatorConstants>;
};

interface ILevel1ChartData {
	totalDiagnoses: number;
	totalInvasive: number;
	totalDataQualityEligibleDiagnoses: number;
	facilities: IDimFacility[];
	hhses: IDimension[];
	mdts: IDimMDT[];
	month: IDimension[];
	year: IDimension[];
	defMastectomyInd: IBreastIndicatorConstants[];

	hadSurgery: { numerator, denominator: number, percentage: number };
	slnbOnT1TumoursWithIndexBCS: { numerator: number, denominator: number, percentage: number; };
	slnbAndAxillaryLymphNodeDissection: { numerator: number, denominator: number, percentage: number; };
	axillaryLymphNodeDissection: { numerator: number, denominator: number, percentage: number; };
	definitiveMastectomy: { numerator: number, denominator: number, percentage: number; };
	mastectomyForT1Tumour: { numerator: number, denominator: number, percentage: number; };
	reExcisionAfterIndexBCS: { numerator: number, denominator: number, percentage: number; };
	conversionOfIndexBCSToMastectomy: { numerator: number, denominator: number, percentage: number; };
	timeFromPathologicalDiagnosisToFirstSurgery45Days: { numerator: number, denominator: number, percentage: number; };
	adjuvantIVSTForAxillaryLNPositiveAgedLessThan70: { numerator: number, denominator: number, percentage: number; };
	neoadjuvantIVST: { numerator: number, denominator: number, percentage: number };
	timeFromDefinitiveSurgeryToFirstIVST45Days: { numerator: number, denominator: number, percentage: number };
	timeToCompletionWithin37Weeks: { numerator: number, denominator: number, percentage: number };
	xrtFollowingDefinitiveBCS: { numerator: number, denominator: number, percentage: number };
	daysToXrtStartDateFromDefinitiveSurgery: { numerator: number, denominator: number, percentage: number };

	reconstructionAfterMastectomy: { numerator: number, denominator: number, percentage: number };
	reconstructionAtMastectomy: { numerator: number, denominator: number, percentage: number };
	// Export
	filteredReferralIdCsv: string;
}

// HACK: chop out the reference array from the shared interface 
type RestrictedSharedChartData = Omit<Omit<IDashboardSharedChartData, "facilities">, "hhses">;

interface ILevel2ChartData extends RestrictedSharedChartData {
	totalDiagnoses: number;
	primarySite: PrimarySite.IHierarchyPlotData;
	morphology: Morphology.IPlotData;
	remoteness: Remoteness.IPlotData;
	hhsOfResidence: HHSOfResidence.IPlotData;
}

export interface IChartData {
	level1: ILevel1ChartData;
	level2: ILevel2ChartData;
}

const BreastFilterKeys: IFilterKeys = {
	//Filter keys for breast indicators
	DiagnosisYear: { key: "DiagnosisYear", label: "Diagnosis Year" },
	DiagnosisMonth: { key: "DiagnosisMonth", label: "Diagnosis Month" },
	MdtYear: { key: "MdtYear", label: "MDT Year" },
	MdtMonth: { key: "MdtMonth", label: "MDT Month" },
	Invasive: { key: "Invasive", label: "Invasive / In-situ" },
	PrimarySite: { key: "PrimarySite", label: "Primary site" },
	Morphology: { key: "Morphology", label: "Morphology" },
	Remoteness: { key: "Remoteness", label: "Rurality" },
	DefinitiveMastectomy: { key: "DefinitiveMastectomy", label: "Definitive Mastectomy" },
	HHSOfResidence: { key: "HHSOfResidence", label: "HHS of residence" }
}

export const DashboardFilterKeys: IFilterKeys = {
	...DashboardSharedFilterKey,
	...BreastFilterKeys
}

export type Filters = IFilters<typeof DashboardFilterKeys>;
export type DashboardFilterFunc = DashboardSharedFilterFunc<IDashboardViewModel>;
export type AddDataFunc = AddDataSharedFunc<IDashboardViewModel, ISourceData>;